import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageShell } from '@/components/ui/PageShell';
import { EliteCard } from '@/components/ui/EliteCard';
import { PrimaryButton } from '@/components/ui/Buttons';
import { useRoleStore } from '@/store/roleStore';
import { db } from '@/lib/db/db';
import { useLiveQuery } from 'dexie-react-hooks';
import { Mail, CheckCircle2, ShieldAlert, Trash2, ArrowLeft } from 'lucide-react';

export function SharePage() {
    const { id: athleteId } = useParams();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [canEdit, setCanEdit] = useState(false);
    const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [errorMsg, setErrorMsg] = useState('');

    const athlete = useLiveQuery(() =>
        athleteId ? db.athletes.get(athleteId) : undefined
    );

    const { activeShares, sentInvites, fetchParentShares, createInvite, revokeInvite } = useRoleStore();

    useEffect(() => {
        if (athleteId) {
            fetchParentShares(athleteId);
        }
    }, [athleteId, fetchParentShares]);

    const handleInvite = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!athleteId || !email.trim()) return;

        setStatus('loading');
        const res = await createInvite(athleteId, email.trim(), canEdit);

        if (res.success) {
            setStatus('success');
            setEmail('');
            setTimeout(() => setStatus('idle'), 3000);
        } else {
            setStatus('error');
            setErrorMsg(res.error || 'Failed to send invite');
        }
    };

    if (!athlete) return <PageShell title="Loading..."><div /></PageShell>;

    return (
        <PageShell title="Manage Access" subtitle={athlete.name}>
            <div className="space-y-6 animate-fade-in-up pb-8">

                <button
                    onClick={() => navigate('/athletes')}
                    className="flex items-center gap-2 text-text-muted hover:text-white transition-colors text-sm font-bold uppercase tracking-widest"
                >
                    <ArrowLeft size={16} /> Back to Athletes
                </button>

                <EliteCard className="p-5">
                    <h3 className="text-white font-black uppercase tracking-widest text-sm mb-4">Invite Coach</h3>

                    <form onSubmit={handleInvite} className="space-y-4">
                        <div className="flex bg-bg-surface border border-white/10 rounded-lg overflow-hidden focus-within:border-gold-500/50 transition-colors">
                            <div className="px-3 flex items-center justify-center text-text-muted">
                                <Mail size={18} />
                            </div>
                            <input
                                type="email"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                placeholder="Coach's Email"
                                className="w-full bg-transparent px-2 py-3 text-white focus:outline-none placeholder:text-text-muted/50 text-sm"
                                required
                            />
                        </div>

                        <label className="flex items-center gap-3 cursor-pointer group">
                            <div className="relative flex items-center justify-center w-5 h-5 rounded border border-white/20 bg-bg-surface group-hover:border-gold-500/50 transition-colors">
                                <input
                                    type="checkbox"
                                    checked={canEdit}
                                    onChange={(e) => setCanEdit(e.target.checked)}
                                    className="appearance-none w-full h-full cursor-pointer"
                                />
                                {canEdit && <CheckCircle2 className="absolute text-gold-500 w-3.5 h-3.5" />}
                            </div>
                            <div>
                                <p className="text-white text-sm font-bold">Allow Editing</p>
                                <p className="text-[10px] text-text-muted">Coach can modify stats and profile</p>
                            </div>
                        </label>

                        {status === 'error' && (
                            <div className="text-red-400 text-xs font-bold bg-red-400/10 p-2 rounded">
                                {errorMsg}
                            </div>
                        )}

                        {status === 'success' && (
                            <div className="text-green-400 text-xs font-bold bg-green-400/10 p-2 rounded">
                                Invite created successfully!
                            </div>
                        )}

                        <PrimaryButton
                            type="submit"
                            disabled={status === 'loading' || !email.trim()}
                            className="w-full py-3"
                        >
                            {status === 'loading' ? 'Creating...' : 'Create Invite'}
                        </PrimaryButton>
                    </form>
                </EliteCard>

                {/* Pending Invites */}
                {sentInvites.length > 0 && (
                    <div>
                        <h3 className="text-text-muted font-black uppercase tracking-widest text-xs mb-3 px-2">Pending Invites</h3>
                        <div className="space-y-3">
                            {sentInvites.map(invite => (
                                <EliteCard key={invite.id} className="p-4 flex items-center justify-between">
                                    <div>
                                        <p className="text-white text-sm font-bold">{invite.invited_email}</p>
                                        <div className="flex items-center gap-2 mt-1">
                                            <span className="text-[10px] text-text-muted bg-white/5 px-2 py-0.5 rounded uppercase font-bold tracking-widest">
                                                Token: <span className="text-gold-500 font-mono">{invite.token}</span>
                                            </span>
                                            {invite.can_edit && <ShieldAlert size={12} className="text-gold-500" />}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => revokeInvite(invite.id)}
                                        className="w-8 h-8 rounded-full bg-red-500/10 text-red-400 flex items-center justify-center hover:bg-red-500/20 transition-colors"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </EliteCard>
                            ))}
                        </div>
                    </div>
                )}

                {/* Active Shares */}
                {activeShares.length > 0 && (
                    <div>
                        <h3 className="text-text-muted font-black uppercase tracking-widest text-xs mb-3 px-2">Active Access</h3>
                        <div className="space-y-3">
                            {activeShares.map((share, idx) => (
                                <div key={idx} className="p-4 rounded-xl border border-white/5 bg-bg-surface/50 flex items-center justify-between">
                                    <p className="text-white text-sm font-bold">Coach {share.coach_user_id.substring(0, 6)}</p>
                                    {share.can_edit && <span className="text-[10px] text-gold-500 uppercase tracking-widest font-bold">Can Edit</span>}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </PageShell>
    );
}
